import { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import usePopupStore from '../store/popupStore';
import useUserStore from '../store/userStore';

// 앱 환경 확인
const isInApp = () => {
    return (
        /yourAppName|AppWebView/i.test(navigator.userAgent) || 
        window.ReactNativeWebView !== undefined
    );
};

function AuthVerificationResult() {
    const navigate = useNavigate();
    const location = useLocation();
    const { updateJoinVerificationInfo } = useUserStore();
    const { showAlert } = usePopupStore();

    useEffect(() => {
        try {
            const query = new URLSearchParams(location.search);
            const data = query.get('data');
            const context = query.get('context');
            
            if (data) {
                const userInfo = JSON.parse(decodeURIComponent(data));
                
                if (userInfo.resultCode === "200") {
                    const verificationInfo = {
                        ci: userInfo.ci || "",
                        di: userInfo.di || "",
                        username: userInfo.username || userInfo.user_name || "",
                        mem_phone: userInfo.mem_phone || "",
                        memberKind: localStorage.getItem('member_kind') || "",
                        gender: userInfo.gender || "",
                        birthdate: userInfo.birthdate || ""
                    };
                    
                    // 앱 환경 처리
                    if (isInApp()) {
                        // 앱을 위한 처리
                        updateJoinVerificationInfo(verificationInfo);
                        
                        // 앱에서는 인증 성공 정보를 로컬스토리지에 저장
                        localStorage.setItem('authVerificationInfo', JSON.stringify(verificationInfo));
                        
                        // localStorage에서 원래 페이지 경로 가져오기
                        const originalPath = localStorage.getItem('auth_return_path');
                        const originalContext = localStorage.getItem('auth_context');
                        
                        
                        // 마이페이지 수정인 경우
                        if (context === 'MyPageEdit' || originalContext === 'MyPageEdit') {
                            // 앱에서의 MyPageEdit 처리
                            // ReactNative와 통신하는 코드 (있는 경우)
                            if (window.ReactNativeWebView) {
                                window.ReactNativeWebView.postMessage(JSON.stringify({
                                    type: 'auth_verification_success',
                                    verificationInfo: verificationInfo
                                }));
                            }
                            
                            // 원래 페이지로 돌아가기 (마이페이지 편집 화면)
                            const returnPath = originalPath || '/mypage/profile/edit';
                            navigate(returnPath);
                            
                            // 사용한 경로 정보 삭제
                            localStorage.removeItem('auth_return_path');
                            localStorage.removeItem('auth_context');
                            return;
                        }
                        
                        // 기타 context에 따른 페이지 이동
                        if (context === 'join') {
                            navigate('/joinForm');
                        } else if (context === 'Idreset' || context === 'PassWordreset') {
                            navigate('/resetPassword');
                        } else if (context === 'IdFind') {
                            navigate('/auth/find/id?verified=true');
                        } else if (context === 'PasswordFind') {
                            const userId = query.get('userId');
                            navigate(`/auth/find/password?verified=true${userId ? `&userId=${userId}` : ''}`);
                        } else if (context === 'Inforeset') {
                            navigate('/mypage');
                        } else {
                            // 저장된 경로가 있으면 그 경로로, 없으면 홈으로
                            navigate(originalPath || '/');
                        }
                        
                        // 사용한 경로 정보 삭제
                        localStorage.removeItem('auth_return_path');
                        localStorage.removeItem('auth_context');
                        return;
                    }
                    
                    // 웹 환경 처리 (기존 코드)
                    if (window.opener) {
                        updateJoinVerificationInfo(verificationInfo);
                        
                        // context에 따라 적절한 페이지로 이동
                        if (context === 'join') {
                            window.opener.location.href = '/joinForm';
                        } else if (context === 'Idreset') {
                            window.opener.location.href = '/resetPassword';
                        } else if (context === 'PassWordreset') {
                            window.opener.location.href = '/resetPassword';
                        } else if (context === 'IdFind') {
                            // 아이디 찾기 처리
                            // 부모 창의 localStorage에 인증 정보 저장
                            window.opener.localStorage.setItem('authVerificationInfo', JSON.stringify(verificationInfo));
                            window.opener.location.href = '/auth/find/id?verified=true';
                        } else if (context === 'PasswordFind') {
                            // 비밀번호 찾기 처리
                            window.opener.localStorage.setItem('authVerificationInfo', JSON.stringify({
                                ...verificationInfo,
                                userId: query.get('userId')
                            }));
                            window.opener.location.href = '/auth/find/password?verified=true';
                        } else if (context === 'Inforeset') {
                            window.opener.location.href = '/mypage';
                        } else if (context === 'MyPageEdit') {
                            // 마이페이지 정보 수정 처리 - 페이지 이동 없이 데이터만 전달
                            window.opener.postMessage({
                                type: 'auth_verification_success',
                                verificationInfo: verificationInfo
                            }, '*');
                            
                            // 현재 창 닫기
                            window.close();
                        }
                        
                        // 현재 창 닫기
                        window.close();
                    } else {
                        // opener가 없는 경우 (직접 URL로 접근한 경우)
                        updateJoinVerificationInfo(verificationInfo);
                        
                        if (context === 'join') {
                            navigate('/joinForm');
                        } else if (context === 'reset') {
                            navigate('/resetPassword');
                        } else {
                            navigate('/');
                        }
                    }
                } else {
                    if (isInApp()) {
                        showAlert({
                            text: userInfo.resultMsg || "인증에 실패했습니다.",
                            closeButton: "확인"
                        });
                        navigate('/joinVerification');
                        return;
                    }
                    
                    if (window.opener) {
                        // 부모 창에 알림 표시
                        showAlert({
                            text: userInfo.resultMsg || "인증에 실패했습니다.",
                            closeButton: "확인"
                        });
                        window.opener.location.href = '/joinVerification';
                        window.close();
                    } else {
                        showAlert({
                            text: userInfo.resultMsg || "인증에 실패했습니다.",
                            closeButton: "확인"
                        });
                        navigate('/joinVerification');
                    }
                }
            } else {
                if (isInApp()) {
                    showAlert({
                        text: "인증 정보가 없습니다. 다시 시도해주세요.",
                        closeButton: "확인"
                    });
                    navigate('/joinVerification');
                    return;
                }
                
                if (window.opener) {
                    showAlert({
                        text: "인증 정보가 없습니다. 다시 시도해주세요.",
                        closeButton: "확인"
                    });
                    window.opener.location.href = '/joinVerification';
                    window.close();
                } else {
                    showAlert({
                        text: "인증 정보가 없습니다. 다시 시도해주세요.",
                        closeButton: "확인"
                    });
                    navigate('/joinVerification');
                }
            }
        } catch (error) {
            console.error("본인인증 결과 처리 중 오류:", error);
            if (isInApp()) {
                showAlert({
                    text: "본인 인증 처리 중 오류가 발생했습니다.",
                    closeButton: "확인"
                });
                navigate('/joinVerification');
            } else {
                if (window.opener) {
                    showAlert({
                        text: "본인 인증 처리 중 오류가 발생했습니다.",
                        closeButton: "확인"
                    });
                    window.opener.location.href = '/joinVerification';
                    window.close();
                } else {
                    showAlert({
                        text: "본인 인증 처리 중 오류가 발생했습니다.",
                        closeButton: "확인"
                    });
                    navigate('/joinVerification');
                }
            }
        }
    }, [location.search, navigate, updateJoinVerificationInfo, showAlert]);

    return null;
}

export default AuthVerificationResult;